/**
 * LocaleConfigService
 *
 * Manages project-level locale configuration in Firestore.
 * Path: projects/{projectId}/settings/localeConfig
 *
 * Follows the FontConfigService pattern.
 *
 * @module locale-config/services
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  onSnapshot,
  runTransaction,
  type Firestore,
  type Unsubscribe,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type {
  CreateProjectLocaleGlossaryInput,
  CreateProjectLocaleProfileInput,
  ProjectLocaleConfig,
  ProjectLocaleGlossary,
  ProjectLocaleProfile,
  ProjectLocaleResourceListOptions,
  UpdateProjectLocaleGlossaryInput,
  UpdateProjectLocaleProfileInput,
} from '../types/localeConfig.types';
import { DEFAULT_LOCALE_CONFIG } from '../types/localeConfig.types';
import {
  assertProfileGlossaryCompatibility,
  assertProjectLocaleConfigForAuthoring,
  LocaleAuthoringError,
  normalizeLocaleResourceId,
  normalizeProjectLocaleGlossary,
  normalizeProjectLocaleProfile,
} from './localeAuthoringValidation';

interface LocaleConfigServiceDeps {
  firestore?: Firestore;
  now?: () => Date;
}

export class LocaleConfigService {
  private readonly firestore: Firestore;
  private readonly now: () => Date;

  constructor(deps: LocaleConfigServiceDeps = {}) {
    this.firestore = deps.firestore ?? CoreFirebaseService.getInstance().firestore;
    this.now = deps.now ?? (() => new Date());
  }

  private getDocRef(projectId: string) {
    return doc(this.firestore, 'projects', projectId, 'settings', 'localeConfig');
  }

  private getProfileRef(projectId: string, profileId: string) {
    return doc(this.firestore, 'projects', projectId, 'settings', 'localeConfig', 'profiles', profileId);
  }

  private getGlossaryRef(projectId: string, glossaryId: string) {
    return doc(this.firestore, 'projects', projectId, 'settings', 'localeConfig', 'glossaries', glossaryId);
  }

  /**
   * Get locale config for a project.
   * Returns defaults if no config exists.
   */
  async getConfig(projectId: string): Promise<ProjectLocaleConfig> {
    try {
      const docRef = this.getDocRef(projectId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return { ...DEFAULT_LOCALE_CONFIG };
      }

      const data = docSnap.data() as ProjectLocaleConfig;
      return {
        version: data.version ?? 1,
        locales: data.locales ?? DEFAULT_LOCALE_CONFIG.locales,
        defaultLocale: data.defaultLocale ?? DEFAULT_LOCALE_CONFIG.defaultLocale,
        updatedAt: data.updatedAt ?? '',
      };
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'LocaleConfigService.getConfig');
      throw error;
    }
  }

  /**
   * Save complete locale config for a project.
   */
  async saveConfig(projectId: string, config: ProjectLocaleConfig): Promise<void> {
    try {
      const docRef = this.getDocRef(projectId);
      const data = JSON.parse(JSON.stringify({
        ...config,
        updatedAt: new Date().toISOString(),
      }));
      await setDoc(docRef, data, { merge: false });
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'LocaleConfigService.saveConfig');
      throw error;
    }
  }

  /**
   * Subscribe to real-time locale config updates.
   * @returns Unsubscribe function
   */
  subscribeToConfig(
    projectId: string,
    callback: (config: ProjectLocaleConfig, metadata: { exists: boolean }) => void,
  ): Unsubscribe {
    const docRef = this.getDocRef(projectId);

    return onSnapshot(
      docRef,
      (docSnap) => {
        if (!docSnap.exists()) {
          callback({ ...DEFAULT_LOCALE_CONFIG }, { exists: false });
          return;
        }

        const data = docSnap.data() as ProjectLocaleConfig;
        callback({
          version: data.version ?? 1,
          locales: data.locales ?? DEFAULT_LOCALE_CONFIG.locales,
          defaultLocale: data.defaultLocale ?? DEFAULT_LOCALE_CONFIG.defaultLocale,
          updatedAt: data.updatedAt ?? '',
        }, { exists: true });
      },
      (error) => {
        CoreFirebaseService.getInstance().logError(
          error,
          'LocaleConfigService.subscribeToConfig',
        );
      },
    );
  }

  async listProfiles(
    projectIdInput: string,
    options: ProjectLocaleResourceListOptions = {},
  ): Promise<ProjectLocaleProfile[]> {
    const projectId = normalizeProjectId(projectIdInput);
    try {
      const [configSnapshot, profilesSnapshot] = await Promise.all([
        getDoc(this.getDocRef(projectId)),
        getDocs(collection(this.firestore, 'projects', projectId, 'settings', 'localeConfig', 'profiles')),
      ]);
      const config = requireAuthoringConfig(configSnapshot.exists(), configSnapshot.data());
      return profilesSnapshot.docs
        .map((snapshot) => {
          const profile = normalizeProjectLocaleProfile(snapshot.data(), projectId, config);
          if (profile.id !== snapshot.id) throw resourceIdMismatch('profile', snapshot.id);
          return profile;
        })
        .filter((profile) => options.includeArchived || profile.status === 'active')
        .sort(sortResources);
    } catch (error) {
      this.logAndRethrow(error, 'LocaleConfigService.listProfiles');
    }
  }

  async createProfile(
    projectIdInput: string,
    input: CreateProjectLocaleProfileInput,
  ): Promise<ProjectLocaleProfile> {
    const projectId = normalizeProjectId(projectIdInput);
    const id = normalizeLocaleResourceId(input.id);
    try {
      return await runTransaction(this.firestore, async (transaction) => {
        const configSnapshot = await transaction.get(this.getDocRef(projectId));
        const profileRef = this.getProfileRef(projectId, id);
        const existingSnapshot = await transaction.get(profileRef);
        if (existingSnapshot.exists()) throw resourceAlreadyExists('profile', id);
        const config = requireAuthoringConfig(configSnapshot.exists(), configSnapshot.data());
        const timestamp = this.now().toISOString();
        const profile = normalizeProjectLocaleProfile({
          ...input,
          id,
          projectId,
          version: 1,
          status: 'active',
          createdAt: timestamp,
          updatedAt: timestamp,
        }, projectId, config);
        if (profile.glossaryRef) {
          const glossarySnapshot = await transaction.get(this.getGlossaryRef(projectId, profile.glossaryRef));
          const glossary = requireGlossarySnapshot(glossarySnapshot.exists(), glossarySnapshot.data(), projectId, config, profile.glossaryRef);
          assertProfileGlossaryCompatibility(profile, glossary);
        }
        transaction.set(profileRef, firestoreData(profile));
        return profile;
      });
    } catch (error) {
      this.logAndRethrow(error, 'LocaleConfigService.createProfile');
    }
  }

  async updateProfile(
    projectIdInput: string,
    profileIdInput: string,
    expectedVersion: number,
    patch: UpdateProjectLocaleProfileInput,
  ): Promise<ProjectLocaleProfile> {
    const projectId = normalizeProjectId(projectIdInput);
    const profileId = normalizeLocaleResourceId(profileIdInput, 'profileId');
    assertExpectedVersion(expectedVersion);
    assertPatchKeys(patch, ['sourceLocale', 'targetLocales', 'context', 'glossaryRef']);
    try {
      return await runTransaction(this.firestore, async (transaction) => {
        const configSnapshot = await transaction.get(this.getDocRef(projectId));
        const profileRef = this.getProfileRef(projectId, profileId);
        const existingSnapshot = await transaction.get(profileRef);
        const config = requireAuthoringConfig(configSnapshot.exists(), configSnapshot.data());
        if (!existingSnapshot.exists()) throw resourceNotFound('profile', profileId);
        const current = normalizeProjectLocaleProfile(existingSnapshot.data(), projectId, config);
        assertDocumentId(current.id, profileId, 'profile');
        assertActiveForUpdate(current.status, 'profile', profileId);
        assertVersion(current.version, expectedVersion, 'profile', profileId);
        const next = normalizeProjectLocaleProfile({
          ...current,
          ...patch,
          glossaryRef: patch.glossaryRef === null ? undefined : (patch.glossaryRef ?? current.glossaryRef),
          version: current.version + 1,
          updatedAt: this.now().toISOString(),
        }, projectId, config);
        if (next.glossaryRef) {
          const glossarySnapshot = await transaction.get(this.getGlossaryRef(projectId, next.glossaryRef));
          const glossary = requireGlossarySnapshot(glossarySnapshot.exists(), glossarySnapshot.data(), projectId, config, next.glossaryRef);
          assertProfileGlossaryCompatibility(next, glossary);
        }
        transaction.set(profileRef, firestoreData(next));
        return next;
      });
    } catch (error) {
      this.logAndRethrow(error, 'LocaleConfigService.updateProfile');
    }
  }

  async archiveProfile(projectIdInput: string, profileIdInput: string, expectedVersion: number): Promise<ProjectLocaleProfile> {
    return this.archiveResource('profile', projectIdInput, profileIdInput, expectedVersion);
  }

  async listGlossaries(
    projectIdInput: string,
    options: ProjectLocaleResourceListOptions = {},
  ): Promise<ProjectLocaleGlossary[]> {
    const projectId = normalizeProjectId(projectIdInput);
    try {
      const [configSnapshot, glossariesSnapshot] = await Promise.all([
        getDoc(this.getDocRef(projectId)),
        getDocs(collection(this.firestore, 'projects', projectId, 'settings', 'localeConfig', 'glossaries')),
      ]);
      const config = requireAuthoringConfig(configSnapshot.exists(), configSnapshot.data());
      return glossariesSnapshot.docs
        .map((snapshot) => {
          const glossary = normalizeProjectLocaleGlossary(snapshot.data(), projectId, config);
          if (glossary.id !== snapshot.id) throw resourceIdMismatch('glossary', snapshot.id);
          return glossary;
        })
        .filter((glossary) => options.includeArchived || glossary.status === 'active')
        .sort(sortResources);
    } catch (error) {
      this.logAndRethrow(error, 'LocaleConfigService.listGlossaries');
    }
  }

  async createGlossary(
    projectIdInput: string,
    input: CreateProjectLocaleGlossaryInput,
  ): Promise<ProjectLocaleGlossary> {
    const projectId = normalizeProjectId(projectIdInput);
    const id = normalizeLocaleResourceId(input.id);
    try {
      return await runTransaction(this.firestore, async (transaction) => {
        const configSnapshot = await transaction.get(this.getDocRef(projectId));
        const glossaryRef = this.getGlossaryRef(projectId, id);
        const existingSnapshot = await transaction.get(glossaryRef);
        if (existingSnapshot.exists()) throw resourceAlreadyExists('glossary', id);
        const config = requireAuthoringConfig(configSnapshot.exists(), configSnapshot.data());
        const timestamp = this.now().toISOString();
        const glossary = normalizeProjectLocaleGlossary({
          ...input,
          id,
          projectId,
          version: 1,
          status: 'active',
          createdAt: timestamp,
          updatedAt: timestamp,
        }, projectId, config);
        transaction.set(glossaryRef, firestoreData(glossary));
        return glossary;
      });
    } catch (error) {
      this.logAndRethrow(error, 'LocaleConfigService.createGlossary');
    }
  }

  async updateGlossary(
    projectIdInput: string,
    glossaryIdInput: string,
    expectedVersion: number,
    patch: UpdateProjectLocaleGlossaryInput,
  ): Promise<ProjectLocaleGlossary> {
    const projectId = normalizeProjectId(projectIdInput);
    const glossaryId = normalizeLocaleResourceId(glossaryIdInput, 'glossaryId');
    assertExpectedVersion(expectedVersion);
    assertPatchKeys(patch, ['sourceLocale', 'targetLocales', 'entries']);
    try {
      return await runTransaction(this.firestore, async (transaction) => {
        const configSnapshot = await transaction.get(this.getDocRef(projectId));
        const glossaryRef = this.getGlossaryRef(projectId, glossaryId);
        const existingSnapshot = await transaction.get(glossaryRef);
        const config = requireAuthoringConfig(configSnapshot.exists(), configSnapshot.data());
        if (!existingSnapshot.exists()) throw resourceNotFound('glossary', glossaryId);
        const current = normalizeProjectLocaleGlossary(existingSnapshot.data(), projectId, config);
        assertDocumentId(current.id, glossaryId, 'glossary');
        assertActiveForUpdate(current.status, 'glossary', glossaryId);
        assertVersion(current.version, expectedVersion, 'glossary', glossaryId);
        const next = normalizeProjectLocaleGlossary({
          ...current,
          ...patch,
          version: current.version + 1,
          updatedAt: this.now().toISOString(),
        }, projectId, config);
        transaction.set(glossaryRef, firestoreData(next));
        return next;
      });
    } catch (error) {
      this.logAndRethrow(error, 'LocaleConfigService.updateGlossary');
    }
  }

  async archiveGlossary(projectIdInput: string, glossaryIdInput: string, expectedVersion: number): Promise<ProjectLocaleGlossary> {
    return this.archiveResource('glossary', projectIdInput, glossaryIdInput, expectedVersion);
  }

  private async archiveResource(
    kind: 'profile',
    projectIdInput: string,
    resourceIdInput: string,
    expectedVersion: number,
  ): Promise<ProjectLocaleProfile>;
  private async archiveResource(
    kind: 'glossary',
    projectIdInput: string,
    resourceIdInput: string,
    expectedVersion: number,
  ): Promise<ProjectLocaleGlossary>;
  private async archiveResource(
    kind: 'profile' | 'glossary',
    projectIdInput: string,
    resourceIdInput: string,
    expectedVersion: number,
  ): Promise<ProjectLocaleProfile | ProjectLocaleGlossary> {
    const projectId = normalizeProjectId(projectIdInput);
    const resourceId = normalizeLocaleResourceId(resourceIdInput, `${kind}Id`);
    assertExpectedVersion(expectedVersion);
    try {
      return await runTransaction(this.firestore, async (transaction) => {
        const configSnapshot = await transaction.get(this.getDocRef(projectId));
        const resourceRef = kind === 'profile'
          ? this.getProfileRef(projectId, resourceId)
          : this.getGlossaryRef(projectId, resourceId);
        const resourceSnapshot = await transaction.get(resourceRef);
        const config = requireAuthoringConfig(configSnapshot.exists(), configSnapshot.data());
        if (!resourceSnapshot.exists()) throw resourceNotFound(kind, resourceId);
        const current = kind === 'profile'
          ? normalizeProjectLocaleProfile(resourceSnapshot.data(), projectId, config)
          : normalizeProjectLocaleGlossary(resourceSnapshot.data(), projectId, config);
        assertDocumentId(current.id, resourceId, kind);
        assertActiveForUpdate(current.status, kind, resourceId);
        assertVersion(current.version, expectedVersion, kind, resourceId);
        const next = {
          ...current,
          status: 'archived' as const,
          version: current.version + 1,
          updatedAt: this.now().toISOString(),
        };
        transaction.set(resourceRef, firestoreData(next));
        return next;
      });
    } catch (error) {
      this.logAndRethrow(error, `LocaleConfigService.archive${kind === 'profile' ? 'Profile' : 'Glossary'}`);
    }
  }

  private logAndRethrow(error: unknown, operation: string): never {
    CoreFirebaseService.getInstance().logError(error, operation);
    throw error;
  }
}

function requireAuthoringConfig(exists: boolean, value: unknown): ProjectLocaleConfig {
  if (!exists) {
    throw new LocaleAuthoringError(
      'missing_project_locale_config',
      'Project locale configuration must be saved before authoring localization profiles or glossaries.',
    );
  }
  return assertProjectLocaleConfigForAuthoring(value);
}

function requireGlossarySnapshot(
  exists: boolean,
  value: unknown,
  projectId: string,
  config: ProjectLocaleConfig,
  glossaryId: string,
): ProjectLocaleGlossary {
  if (!exists) throw resourceNotFound('glossary', glossaryId);
  const glossary = normalizeProjectLocaleGlossary(value, projectId, config);
  assertDocumentId(glossary.id, glossaryId, 'glossary');
  return glossary;
}

function normalizeProjectId(value: unknown): string {
  if (typeof value !== 'string' || !value.trim() || value.length > 256 || value.includes('/')) {
    throw new LocaleAuthoringError('invalid_project_id', 'projectId must be a non-empty project identifier.');
  }
  return value.trim();
}

function assertExpectedVersion(value: number): void {
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new LocaleAuthoringError('invalid_expected_version', 'expectedVersion must be a positive integer.');
  }
}

function assertPatchKeys(value: object, allowed: string[]): void {
  const keys = Object.keys(value);
  if (!keys.length) throw new LocaleAuthoringError('empty_update', 'At least one field must be updated.');
  const unknown = keys.filter((key) => !allowed.includes(key));
  if (unknown.length) {
    throw new LocaleAuthoringError('unknown_update_field', `Unknown update field(s): ${unknown.join(', ')}.`);
  }
}

function assertDocumentId(actual: string, expected: string, kind: string): void {
  if (actual !== expected) throw resourceIdMismatch(kind, expected);
}

function assertActiveForUpdate(status: string, kind: string, id: string): void {
  if (status !== 'active') {
    throw new LocaleAuthoringError('resource_archived', `${kind} ${id} is archived and cannot be changed.`);
  }
}

function assertVersion(actual: number, expected: number, kind: string, id: string): void {
  if (actual !== expected) {
    throw new LocaleAuthoringError(
      'resource_version_conflict',
      `${kind} ${id} changed after it was loaded. Refresh and try again.`,
    );
  }
}

function resourceAlreadyExists(kind: string, id: string): LocaleAuthoringError {
  return new LocaleAuthoringError('resource_already_exists', `${kind} ${id} already exists.`);
}

function resourceNotFound(kind: string, id: string): LocaleAuthoringError {
  return new LocaleAuthoringError('resource_not_found', `${kind} ${id} was not found in this project.`);
}

function resourceIdMismatch(kind: string, id: string): LocaleAuthoringError {
  return new LocaleAuthoringError('resource_id_mismatch', `${kind} document ${id} has a mismatched id.`);
}

function firestoreData<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function sortResources<T extends { id: string }>(left: T, right: T): number {
  return left.id.localeCompare(right.id);
}

// Singleton
let serviceInstance: LocaleConfigService | null = null;

export function getLocaleConfigService(): LocaleConfigService {
  if (!serviceInstance) {
    serviceInstance = new LocaleConfigService();
  }
  return serviceInstance;
}
